import React, { Suspense } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import {
  getPathFromState as defaultGetPathFromState,
  NavigationContainer,
  useNavigation,
} from '@react-navigation/native';
import { navigationRef } from 'shared/src/utils/navigator/navigationRef';
import { AppDownloadBanner } from './components/AppDownloadBanner';
import { MainScreen } from './screens/MainScreen';
import type { ScreenItem } from 'shared/src/types';
import { MarketScreens } from 'market/src/Navigator';
import { GoodsScreens } from 'goods/src/Navigator';
import { MyPageScreens } from 'mypage/src/Navigator';
import { HomeScreens, HomeTabScreens } from 'home/src/Navigator';
import { SearchScreens } from 'search/src/Navigator';
import { ScreensPageScreens } from 'screens/src/Navigator';
import { LoginScreens } from 'login/src/Navigator';
import { ErrorBoundary } from 'shared/src/component';
import { LoadingView } from 'ably-app/src/components/LoadingView';
import * as Sentry from '@sentry/react';
import type { AppProps } from './App';
import { RetainedStateProvider } from 'shared/src/hooks/retainedState';
import { FocusAwareModalProvider } from 'shared/src/context';
import { BottomTabNavigator } from 'home/src/navigation/BottomTabNavigation';
// WEBVIEW 탭은 RN 화면(HomeTabScreens)이 아니라 TabWebViewScreen 을 쓰므로 경로(URL)를 따로
// 지정한다. AppDownloadBanner 의 `ably://main/:tab_id` 도 같은 표를 쓴다.
import { WEBVIEW_TAB_PATHS } from './tabRoutePaths';

const Stack = createNativeStackNavigator();

/**
 * 주소(URL)에 남기지 않을 route params.
 *
 * react-navigation 은 path 패턴에 없는 params 를 전부 쿼리스트링으로 붙인다. 화면 동작에는
 * 쓰지만 주소에 드러날 이유가 없는 값들이 그대로 노출돼(`?extras=%7B...%7D&instanceId=...`)
 * 주소가 길고 지저분해진다.
 *
 * ⚠️ **없어도 화면이 동작하는 params 만** 넣을 것. 주소에서 지우면 새로고침·링크 공유로 다시
 * 들어올 때 그 값이 없다. `instanceId` 는 진입할 때마다 새로 만드는 값이고, `extras` 는
 * 네이티브에서 화면을 열 때 실어 보내는 통로라 웹 주소로 재진입할 때는 필요하지 않다.
 * (화면 식별에 필수인 `screen_name` 같은 건 절대 넣으면 안 된다)
 */
const URL_HIDDEN_PARAMS: ReadonlySet<string> = new Set([
  'extras',
  'instanceId',
]);

type NavigationStateLike = Parameters<typeof defaultGetPathFromState>[0];

const omitHiddenParams = (params: object | undefined): object | undefined => {
  if (params == null) return params;
  const entries = Object.entries(params).filter(
    ([key]) => !URL_HIDDEN_PARAMS.has(key),
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/**
 * 중첩 state 까지 내려가며 `URL_HIDDEN_PARAMS` 를 털어낸 state 를 만든다(원본은 건드리지 않는다).
 * 하단탭처럼 중첩된 화면도 결국 focused route 의 params 가 쿼리로 나가므로 재귀가 필요하다.
 */
const stripHiddenParams = (state: NavigationStateLike): NavigationStateLike => {
  const routes = state.routes.map(route => ({
    ...route,
    params: omitHiddenParams(route.params),
    ...(route.state ? { state: stripHiddenParams(route.state) } : {}),
  }));

  // `routes` 는 (완전 state | 부분 state) 유니온이라 map 결과가 그대로 재대입되지 않는다
  // (`key` 의 optional 여부가 갈린다). 바꾸는 건 params/state 뿐이고 나머지 필드는 그대로
  // 흘리므로 입력 타입으로 되돌린다.
  return { ...state, routes } as NavigationStateLike;
};

const screenMetadata: ScreenItem[] = [
  ...MarketScreens,
  ...GoodsScreens,
  ...MyPageScreens,
  ...SearchScreens,
  ...ScreensPageScreens,
  ...HomeScreens,
  ...LoginScreens,
];

// const BottomTabs = createBottomTabNavigator();
//
// const BottomTabNavigator = () => {
//   return (
//     <BottomTabs.Navigator
//       screenOptions={{ headerShown: false }}
//       tabBar={props => <BottomTabBar {...props} />}
//     >
//       {HomeTabScreens.map((screen: ScreenItem) => (
//         <BottomTabs.Screen
//           name={screen.name}
//           component={screen.component}
//           options={{
//             title:
//               screen.name === 'Home'
//                 ? '에이블리 | 전상품 무료배송'
//                 : screen.options?.tabBarLabel || screen.name,
//             tabBarIcon: screen.options?.icon,
//             tabBarLabel: screen.options?.tabBarLabel,
//           }}
//         />
//       ))}
//     </BottomTabs.Navigator>
//   );
// };

const linkingScreens = {
  tab: {
    path: '/',
    // 하단탭 route 는 mainTabs 의 screen_name 으로 등록되므로, linking 키도 screenName 으로 맞춘다.
    // (screen_name 이 없는 항목은 탭 route 로 등록되지 않으므로 제외)
    screens: {
      ...HomeTabScreens.reduce<Record<string, string>>((acc, cur) => {
        if (cur.screenName != null) {
          acc[cur.screenName] = cur.path;
        }
        return acc;
      }, {}),
      ...WEBVIEW_TAB_PATHS,
    },
  },
  ...screenMetadata.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.name] = cur.path;
    return acc;
  }, {}),
};

export const NavigationWithModalProvider: React.FC<AppProps> = () => {
  return (
    <NavigationContainer
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) => {
          return options?.title ?? route?.name ?? '에이블리 | 전 상품 무료배송';
        },
      }}
      linking={{
        config: {
          screens: linkingScreens,
        },
        prefixes: ['https://mobile.a-bly.com'],
        // 주소를 만들 때만 일부 params 를 숨긴다(위 URL_HIDDEN_PARAMS 주석 참고).
        // 파싱(getStateFromPath)은 건드리지 않으므로 그 값이 붙어 들어오면 그대로 받는다.
        getPathFromState: (state, config) =>
          defaultGetPathFromState(stripHiddenParams(state), config),
      }}
    >
      <AsyncErrorBoundary>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            title: '에이블리 | 전 상품 무료배송',
          }}
          screenLayout={({ children, route }) => {
            return (
              <RetainedStateProvider key={route.key}>
                {/*
                  `hostsModals={false}` 인 화면(Home/Category/Like/로그인 등 — 모달을 탭 UI
                  위로 띄워야 하는 화면)은 자체 ModalProvider 가 없어 이 Provider 가 모달을
                  잡는다. 없으면 `useModal` 이 context 기본값(no-op)을 받아 모달이 조용히
                  안 뜬다.

                  네이티브(`Navigation.native.tsx`)도 같은 위치에 ModalProvider 를 둔다.
                  웹만 focus 게이팅이 필요해 `FocusAwareModalProvider` 를 쓴다 — 모달이
                  document.body 로 portal 돼서 화면 이동 시 display:none 이 닿지 않는다.
                */}
                <FocusAwareModalProvider>
                  <AppDownloadBanner />
                  {children}
                </FocusAwareModalProvider>
              </RetainedStateProvider>
            );
          }}
        >
          <Stack.Screen
            options={{
              headerShown: false,
            }}
            name={'tab'}
            component={BottomTabNavigator}
          />
          <Stack.Screen name={'main'} component={MainScreen} />
          {screenMetadata.map(screen => {
            return (
              <Stack.Screen
                name={screen.name}
                component={screen.component}
                options={screen.options}
              />
            );
          })}
        </Stack.Navigator>
      </AsyncErrorBoundary>
    </NavigationContainer>
  );
};

const AsyncErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigator = useNavigation();
  return (
    <ErrorBoundary
      navigation={navigator}
      onReportError={(error, errorInfo) => {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: errorInfo.componentStack } },
        });
      }}
    >
      <Suspense fallback={<LoadingView />}>{children}</Suspense>
    </ErrorBoundary>
  );
};
